import { startCase } from 'lodash-es'

import type { ParcelQuote } from './types'

export type ParcelRateGroup = {
  carrierName: string
  /** Lowest price first. */
  quotes: ParcelQuote[]
}

export const getCarrierName = (quote: ParcelQuote): string => {
  if (quote.carrier?.name) return quote.carrier.name
  if (quote.carrier?.code) return startCase(quote.carrier.code.toLowerCase())
  return ''
}

export const getServiceLevel = (quote: ParcelQuote): string | null => {
  const serviceLevel = quote.serviceLevel
  if (serviceLevel?.name) return serviceLevel.name
  if (serviceLevel?.token) return startCase(serviceLevel.token.toLowerCase())
  if (quote.carrier?.code) return startCase(quote.carrier.code.toLowerCase())
  return null
}

const byPrice = (a: ParcelQuote, b: ParcelQuote): number =>
  a.totalPrice - b.totalPrice ||
  (getServiceLevel(a) ?? '').localeCompare(getServiceLevel(b) ?? '') ||
  (a._id ?? '').localeCompare(b._id ?? '')

/**
 * One group per carrier, rates sorted by price inside each group, and groups
 * ordered by their cheapest rate. Ties fall back to service names and ids, so
 * the same rates always come out in the same order.
 */
export const groupParcelRates = (quotes: ParcelQuote[]): ParcelRateGroup[] => {
  // Groups are created in the order of their cheapest rate, since the rates are
  // walked cheapest first.
  const groups = new Map<string, ParcelQuote[]>()
  for (const quote of [...quotes].sort(byPrice)) {
    const carrierName = getCarrierName(quote)
    groups.set(carrierName, [...(groups.get(carrierName) ?? []), quote])
  }
  return [...groups.entries()].map(([carrierName, groupQuotes]) => ({
    carrierName,
    quotes: groupQuotes,
  }))
}
